import { useEffect, useRef, useState } from 'react'
import { Document } from '@contentful/rich-text-types'

import Collapsible from '@/components/ui/collapsible'
import RichText from '@/components/ui/rich-text'
import Image from '@/components/ui/image'

import AngleIcon from '@/icons/bwi/angle-up.svg'

import { cx, slugify } from '@/utils/strings'
import { TypeComponentAccordionItem } from '@/types/ctf'

type AccordionItemProps = {
  title: string
  content?: Document | string | null
  isOpen?: boolean
  color?: TypeComponentAccordionItem<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']['color']
  onToggle?: (isOpen: boolean) => void
  /** Allow the open state to be managed internally */
  nonControlled?: boolean
}

export default function AccordionItem({
  title,
  content,
  isOpen: _isOpen,
  color = 'White',
  onToggle,
  nonControlled = false,
}: AccordionItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [internalIsOpen, setInternalIsOpen] = useState(!!_isOpen)
  const isOpen = nonControlled ? internalIsOpen : _isOpen
  const id = slugify(title)
  const disableAnimationRef = useRef(false)

  useEffect(() => {
    // Open if the hash matches the ID of this accordion item
    if (location.hash === `#${id}`) {
      disableAnimationRef.current = true
      if (nonControlled) setInternalIsOpen(true)
      else onToggle?.(true)
    }
  }, [])

  return (
    <div
      id={id}
      ref={ref}
      className={cx(
        'rounded-lg w-full mb-3 border bg-bw-gray-teal dark:bg-secondary-800',

        !isOpen && 'hover:border-primaryBlue transition-colors'
      )}
    >
      <button
        type="button"
        className={cx(
          'flex gap-6 w-full justify-between items-start font-heading text-left py-5 px-6 text-lg lg:text-xl rounded-lg',
          'transition-colors duration-200',
          isOpen && color === 'White' && 'hover:bg-primary-100/80 dark:hover:bg-secondary-700',
          isOpen && color === 'Light Gray' && 'hover:bg-primary-100/80 dark:hover:bg-secondary-700',
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primaryBlue'
        )}
        onClick={() => {
          if (nonControlled) setInternalIsOpen(!isOpen)
          onToggle?.(!isOpen)
        }}
      >
        <span className={cx(!isOpen && 'text-primaryBlue')}>{title}</span>
        <AngleIcon
          className={cx('w-4 shrink-0 mt-1 transition duration-200', isOpen ? '' : `rotate-180 text-primaryBlue`)}
        />
      </button>
      <Collapsible
        className="px-6"
        isOpen={isOpen}
        disableAnimation={disableAnimationRef.current}
        onAnimationComplete={() => {
          disableAnimationRef.current = false
        }}
      >
        <div className="pt-1.5">
          {typeof content === 'string' || !content ? (
            content
          ) : (
            <RichText
              body={content}
              renderImage={(ref) => (
                <div className="pt-6 pb-12 m-auto w-full [&_svg]:w-full [&_figure]:w-full [&_img]:w-full">
                  <Image image={ref} />
                </div>
              )}
            />
          )}
        </div>
      </Collapsible>
    </div>
  )
}
